import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import {
  FormProvider,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { AuthContext } from '../../../auth';
import Field from '../../general/rhf/Field';
import GlassSelection from '../FdForm/GlassSelection';
import { usePriceUpdate } from './lib';

const DynamicList = ({ name, label, itemLabel, validateItem }) => {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  return (
    <fieldset className="mb-4">
      <legend>{label}</legend>
      {fields.map((field, index) => {
        const error =
          errors[name] && errors[name][index] ? errors[name][index].value : null;

        return (
          <div key={field.id} className="d-flex align-items-start mb-3">
            <label className="w-100 mb-0">
              <span className="d-block mb-2">{`${itemLabel} ${index + 1}`}</span>
              <input
                type="number"
                step="any"
                className={`form-control ${error ? 'is-invalid' : ''}`}
                {...register(`${name}.${index}.value`, validateItem(index))}
              />
              <div className="invalid-feedback font-weight-normal">
                {error && error.message}
              </div>
            </label>
            <button
              type="button"
              className="btn btn-outline-secondary ml-2 mt-4"
              onClick={() => remove(index)}
              disabled={fields.length === 1}
            >
              Remove
            </button>
          </div>
        );
      })}
      <button
        type="button"
        className="btn btn-outline-primary btn-sm"
        onClick={() => append({ value: '' })}
      >
        Add {itemLabel}
      </button>
    </fieldset>
  );
};

DynamicList.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  itemLabel: PropTypes.string.isRequired,
  validateItem: PropTypes.func.isRequired,
};

const Form = ({ context, marks, glassCompositions, frameColors, markups }) => {
  const { currentUser } = useContext(AuthContext);
  const autoPrice = useWatch({
    name: 'autoPrice',
    control: context.control,
  });

  usePriceUpdate(context, glassCompositions, markups);

  const positiveNumber = {
    required: 'Required',
    valueAsNumber: true,
    validate: {
      positive: (value) => value > 0 || 'Must be greater than 0',
    },
  };

  const elevationValidator = (index) => ({
    required: 'Required',
    valueAsNumber: true,
    validate: {
      positive: (value) => value > 0 || 'Must be greater than 0',
      increasing: (value) => {
        const previous = context.getValues(`elevations.${index - 1}.value`);
        const next = context.getValues(`elevations.${index + 1}.value`);

        if (
          index !== 0 &&
          previous !== '' &&
          typeof previous !== 'undefined' &&
          value <= parseFloat(previous)
        ) {
          return 'Must be greater than previous elevation';
        }

        if (
          next !== '' &&
          typeof next !== 'undefined' &&
          !Number.isNaN(parseFloat(next)) &&
          value >= parseFloat(next)
        ) {
          return 'Must be less than next elevation';
        }

        return true;
      },
    },
  });

  return (
    <FormProvider {...context}>
      <form>
        <Field name="estimateId" type="hidden" />
        <Field name="id" type="hidden" />
        <Field
          name="mark"
          label="Mark"
          register={{
            required: 'Required',
            validate: {
              taken: (value) =>
                marks.includes(value.toUpperCase())
                  ? 'Mark is already in use'
                  : true,
            },
          }}
        />
        <Field
          name="autoPrice"
          type={currentUser.internal ? 'checkbox' : 'hidden'}
          label="Auto Price"
        />
        <Field
          name="price"
          label="Price per sqft"
          type={currentUser.internal ? 'number' : 'hidden'}
          readOnly={!!autoPrice}
          register={{ required: 'Required' }}
        />
        <Field
          type="number"
          name="qty"
          label="Qty"
          register={{ required: 'Required' }}
        />
        <GlassSelection glassCompositions={glassCompositions} />
        <Field type="select" name="frameColorId" label="Frame Color">
          {frameColors.map(({ id, color }) => (
            <option key={id} value={id}>
              {color}
            </option>
          ))}
        </Field>
        <DynamicList
          name="widths"
          label="Horizontal Lites"
          itemLabel="Width"
          validateItem={() => positiveNumber}
        />
        <DynamicList
          name="elevations"
          label="Vertical Lites"
          itemLabel="Height From Floor"
          validateItem={elevationValidator}
        />
      </form>
    </FormProvider>
  );
};

Form.propTypes = {
  context: PropTypes.object.isRequired,
  marks: PropTypes.arrayOf(PropTypes.string).isRequired,
  glassCompositions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      composition: PropTypes.string.isRequired,
    })
  ).isRequired,
  frameColors: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    })
  ).isRequired,
  markups: PropTypes.array.isRequired,
};

export default Form;
